import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-01.svg";
import ShoppingBagFilledIcon from "@redotech/redo-icons/shopping-bag-01_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TAB_PARAM } from "@redotech/redo-merchant-fulfillment-app/outbound-order";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  ORDERS_ICON,
  ORDERS_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { OrderEditsItem } from "./order-edits";

// Keep Orders inactive when an order came from a fulfillment-group view.
const ORDERS_PATH_ROOT_INACTIVE = "\0";

export function useOrdersPrimaryNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const canViewOrders = useHasPermission(OrderManagementPermissions.VIEW_ORDER);

  if (!canViewOrders) {
    return null;
  }

  const href = routes.getOrdersOutboundLabelsSubRoute("orders");
  // Orders normally wins route matching, except when opened from a fulfillment view.
  const cameFromFulfillmentView = searchParams.has(TAB_PARAM);
  const pathRoot = cameFromFulfillmentView
    ? ORDERS_PATH_ROOT_INACTIVE
    : `${routes.ordersOutboundLabelsHref}/order`;

  const primaryTab: LinkPrimaryTab = {
    id: "orders",
    hasNotification: false,
    UnfilledIcon: ShoppingBagIcon,
    FilledIcon: ShoppingBagFilledIcon,
    type: "link",
    pathRoot,
    href,
    friendlyName: t("Orders", {
      context: "primary navigation section for merchant sales orders",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "orders",
    name: t("Orders", {
      context: "primary navigation section for merchant sales orders",
    }),
    NavContent: OrdersAreaSecondaryNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: true,
    isCore: true,
  };
}

const ORDER_VIEWS = [
  { key: "all" },
  { key: "protected" },
  { key: "fulfilled" },
  { key: "partially_fulfilled" },
  { key: "not_fulfilled" },
] as const;

const OrdersAreaSecondaryNavigation = memo(
  function OrdersAreaSecondaryNavigation() {
    const { t } = useTranslation();
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: t("Orders", {
        context: "primary navigation section for merchant sales orders",
      }),
    });

    return (
      <TreeContext.Provider value={nodeId}>
        {/* Orders has no dashboard; the orders list is its landing page. */}
        <OrdersWithViewsItem />
        <OrderEditsItem />
      </TreeContext.Provider>
    );
  },
);

const OrdersWithViewsItem = memo(function OrdersWithViewsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const ordersListPath = routes.getOrdersOutboundLabelsSubRoute("orders");
  const isOnOrdersArea = location.pathname.startsWith(ordersListPath);
  const isOnOrdersListPage =
    location.pathname === ordersListPath ||
    location.pathname === `${ordersListPath}/`;
  const currentFilter = searchParams.get("filter") ?? "all";

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isOnOrdersArea}
      FilledIcon={ORDERS_ICON_FILLED}
      friendlyName={t("Orders", {
        context:
          "order, fulfillment, claims, and warehouse operations navigation label",
      })}
      id="orders"
      isFolder
      UnfilledIcon={ORDERS_ICON}
    >
      {ORDER_VIEWS.map((view) => (
        <OrdersFilterViewItem
          href={
            view.key === "all"
              ? ordersListPath
              : `${ordersListPath}?filter=${view.key}`
          }
          key={view.key}
          name={
            view.key === "all"
              ? t("All", {
                  context: "order status navigation filter showing every order",
                })
              : view.key === "protected"
                ? t("Covered", {
                    context:
                      "order status navigation filter for orders covered by Redo",
                  })
                : view.key === "fulfilled"
                  ? t("Fulfilled", {
                      context:
                        "order status navigation filter; plural adjective describing orders that have been fully picked, packed, and shipped",
                    })
                  : view.key === "partially_fulfilled"
                    ? t("Partially fulfilled", {
                        context:
                          "order status navigation filter for partially fulfilled orders",
                      })
                    : t("Unfulfilled", {
                        context:
                          "order status navigation filter for unfulfilled orders",
                      })
          }
          selected={isOnOrdersListPage && currentFilter === view.key}
          viewKey={view.key}
        />
      ))}
    </RedoSecondaryNavItem>
  );
});

const OrdersFilterViewItem = memo(function OrdersFilterViewItem({
  href,
  name,
  selected,
  viewKey,
}: {
  href: string;
  name: string;
  selected: boolean;
  viewKey: string;
}) {
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={name}
      id={`orders-view-${viewKey}`}
      selected={selected}
    />
  );
});
